import React from "react";
import FullCalendar from "@fullcalendar/react";
import { config } from "@fullcalendar/core/internal";
import resourceTimelinePlugin from "@fullcalendar/resource-timeline";
import interactionPlugin from "@fullcalendar/interaction";
import _ from "lodash";
import { CalendarOptions, EventContentArg, EventInput, SlotLabelContentArg } from "@fullcalendar/core";
import moment from "moment";
import "./Calendar.scss";
import { ResourceInput } from "@fullcalendar/resource";
import { getDataProps } from "../../../shared/utilities";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";

config.MAX_TIMELINE_SLOTS = 1_000_000;

type CalendarProps = CalendarOptions & {
    name?: string;
    resources: ResourceInput[];
    events: EventInput[];
};

const Calendar = (props: CalendarProps) => {
    const { events, resources, eventContent: _eventContent, ...rest } = props;

    const fromDate = moment(_.min((events ?? []).map(e => e.start))).startOf("month").toDate();
    const toDate = moment(_.max((events ?? []).map(e => e.end))).endOf("month").toDate();

    const calendarRef = React.useRef<FullCalendar>(null);

    const defaultEventContent = React.useCallback((arg: EventContentArg) => {
        const { title } = arg.event;

        return (
            <div className="fc-event-main-frame">
                <div className="fc-event-title-container">
                    <div className="fc-event-title fc-sticky">
                        <b>{title}</b>
                    </div>
                </div>
            </div>
        );
    }, []);

    const eventContent = _eventContent ?? defaultEventContent;

    const getAdditionalSlotClassNames = React.useCallback((arg: SlotLabelContentArg) => {
        const result = [];

        const { date } = arg;
        const day = moment(date);

        if (day.date() === 1) {
            result.push("fc-first-day-of-month");
        }

        if (day.date() === day.daysInMonth()) {
            result.push("fc-last-day-of-month");
        }

        return result;
    }, []);

    // Add data attributes to the calendar element
    React.useEffect(() => {
        Object.entries(getDataProps(props)).forEach(([key, value]) => {
            calendarRef.current?.["elRef"]?.current?.setAttribute(key, value);
        });
    }, [props]);    

    return (
        <FullCalendar
            ref={calendarRef}
            schedulerLicenseKey="CC-Attribution-NonCommercial-NoDerivatives"
            plugins={[resourceTimelinePlugin, interactionPlugin]}

            events={events}
            resources={resources}

            slotLaneClassNames={getAdditionalSlotClassNames}
            visibleRange={{ start: fromDate, end: toDate }}
            height="100%"

            resourceOrder={null} // Retain original resource order - don't sort by title
            editable={true}
            scrollTimeReset={false} // Don't reset the scroll position each time the calendar input is updated
            eventMinWidth={1} // Events can be rendered arbitrarily small by default

            eventContent={eventContent}

            {...rest}
        />
    );
};

(Calendar as DocumentedComponent).metadata = {
    description: "The `Calendar` component can be used to display event data in multiple views (Month, TimeGrid, List, DayGrid, Multi-Month Stack, Multi-Month Grid, Timeline, Resource Timeline, Vertical Resource, etc.).  This component is a thin wrapper around the [FullCalendar](https://fullcalendar.io/) third-party component.  Please see that component's documentation for more information on the available options and views.",
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        { name: "events", type: "object", exactType: "{ id: string, resourceId: string, title: string, start: Date, end: Date, [key: string]: any }[]", description: `The events to display on the calendar.  See below for the basic structure of \`Event\`.
            
### Event Fields:

| Name | Type | Description |
|------|------|-------------|
| \`id\` | \`string\` | The unique id for this event.  Optional, but highly recommended if editing functionality is used. |
| \`resourceId\` | \`string\` | The id of the resource that this event is linked to.  Optional, but required if using the \`resourceTimeline\` view. |
| \`title\` | \`string\` | The title for this event.  Optional. |
| \`start\` | \`Date\` | The start date/time for this event. |
| \`end\` | \`Date\` | The end date/time for this event. |
| \`[key: string]\` | \`any\` | Any additional metadata can be added directly to the event object. |
`
        },
        { name: "resources", type: "object", exactType: "{ id: string, title: string, [key: string]: any }[]", description: `The resources to display on the calendar.  Depending on the desired view, this may be a required input prop.  See below for the basic structure of \`Resource\`.
            
### Resource Fields:

| Name | Type | Description |
|------|------|-------------|
| \`id\` | \`string\` | The unique id for this resource. |
| \`title\` | \`string\` | The title for this resource. |
| \`[key: string]\` | \`any\` | Any additional metadata can be added directly to the resource object. |
` },
        { name: "editable", type: "boolean", description: "If `false`, the calendar not allow events to be dragged or resized.  Optional - defaults to `true`." },
    ]
};

export { Calendar };